import { Children, forwardRef, type CSSProperties, type ReactNode } from 'react';
import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Table from '../../../components/table/table';
import CheckLightResume from '../CheckLight/ChecklightResume';
import LidecoSemaforo from '../CheckLight/LidecoSemaforo';
import {
  balanceInfo,
  clientData,
  documentData,
  riskData,
  StandardizedBalanceData,
  ReferrerName,
  applicationData,
  indicator,
  SgrDistributionResponseItem,
} from '../../../types/interfaces';
import { economicActivities } from '../../../mocks/EconomicActivities';
import { ivaStatuses } from '../../../mocks/IvaStatuses';
import { legalEntities } from '../../../mocks/LegalEntities';
import { otherLegalEntities } from '../../../mocks/OtherLegalEntities';
import { CURRENCY_CODES } from '../../../utils/Constants';
import { formatCurrency } from '../../../utils/Masks';
import Icon from '../../../components/icon/Icon';
import {
  resolveSelectableProductLineLabel,
  UNAVAILABLE_PRODUCT_LINE_LABEL,
} from '../../../utils/productLines';
import { getManagementDisplayLabel } from '../../../utils/managementDisplay';
import { getOfficerDisplayLabel } from '../../../utils/officerDisplay';
import {
  resolveSgrGrandDisplayTotals,
  resolveSgrGroupDisplayTotal,
} from '../../../utils/sgrDistributionTotals';
import RiskCreditoMaximoReadOnly from '../CreditoMaximo/RiskCreditoMaximoReadOnly';
import type { CreditoMaximoState } from '../CreditoMaximo/CreditoMaximo.types';
import type { SelectableProductLine } from '../../../utils/productLines';
import { useConfig } from '../../../context/ConfigContext';
import { useClientTheme } from '../../../context/ThemeContext';
import { normalizeDgiStatus } from '../../Origination/normalizeUruguayClientData';

const UY_PDF_GRID_3: CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
};

const UY_PDF_GRID_DATOS_GENERALES: CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) minmax(0, 0.55fr)',
};

const UY_PDF_ROW_GAP = '16px';

const formatSummaryDate = (
  dateStr: string | null | undefined,
  separator: '/' | '-' = '-',
): string => {
  if (!dateStr?.trim()) return '—';
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-');
    return `${day}${separator}${month}${separator}${year}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed) && separator === '-') {
    return trimmed.replace(/\//g, '-');
  }
  return trimmed;
};

const formatBooleanAsYesNo = (value: boolean | null | undefined): string =>
  value === true ? 'Sí' : value === false ? 'No' : '—';

const padToGridColumns = (cells: ReactNode[], columnCount: number) => {
  const padded = [...cells];
  while (padded.length < columnCount) {
    padded.push(<span key={`grid-empty-${padded.length}`} aria-hidden='true' />);
  }
  return padded;
};

interface PdfFieldProps {
  label: string;
  value?: ReactNode;
}

const PdfField = ({ label, value }: PdfFieldProps) => {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <Card display='flex' gap='4px' width='100%' style={{ minWidth: 0 }}>
      <P type='2' variant='neutral' weight='bold' withMargin={false}>
        {label}
      </P>
      <P type='2' variant='neutral' withMargin={false}>
        {display}
      </P>
    </Card>
  );
};

interface PdfFieldRowProps {
  children: ReactNode;
  variant?: 'threeCol' | 'datosGenerales';
}

const PdfFieldRow = ({ children, variant = 'threeCol' }: PdfFieldRowProps) => {
  const columnCount = variant === 'datosGenerales' ? 4 : 3;
  return (
    <Card
      display='grid'
      gap='24px'
      width='100%'
      style={
        variant === 'datosGenerales' ? UY_PDF_GRID_DATOS_GENERALES : UY_PDF_GRID_3
      }
    >
      {padToGridColumns(Children.toArray(children), columnCount)}
    </Card>
  );
};

interface RiskAnalysisPdfProps {
  clientData: clientData;
  riskData: riskData[] | undefined;
  balanceData?: balanceInfo | StandardizedBalanceData;
  postBalanceData?: any;
  debts?: any;
  documents?: documentData[];
  cuit?: string;
  referrerNames?: Array<{ code: string; name: string }>;
  officers?: Array<{ code: string; label: string }>;
  productLines?: Array<{ code: string; label: string }>;
  managements?: Array<{ id: number; label: string }>;
  isManagementSelectionEnabled?: boolean;
  isOriginationDataModalEnabled?: boolean;
  showDualBalance?: boolean;
  previousBalanceData?: balanceInfo | StandardizedBalanceData;
  previousBpbIndicators?: indicator[];
  sgrDistributions?: SgrDistributionResponseItem[];
  isUruguayEnabled?: boolean;
  localCurrencyCode?: string;
  creditoMaximoState?: CreditoMaximoState | null;
  creditoProductLines?: SelectableProductLine[];
  creditoSuperaMaximo?: boolean;
  isQuotationsEnabled?: boolean;
  creditoAvailableAssets?: Array<{ code: string; name: string; saleValue?: number | string }>;
}

const RiskAnalysisPdf = forwardRef<HTMLDivElement, RiskAnalysisPdfProps>(
  (
    {
      clientData,
      riskData,
      balanceData,
      postBalanceData,
      debts,
      documents,
      cuit,
      referrerNames = [],
      officers = [],
      productLines = [],
      managements = [],
      showDualBalance = false,
      previousBalanceData,
      previousBpbIndicators,
      sgrDistributions = [],
      localCurrencyCode = CURRENCY_CODES.ars,
      creditoMaximoState = null,
      creditoProductLines = [],
      creditoSuperaMaximo = false,
      isQuotationsEnabled = true,
      creditoAvailableAssets = [],
    },
    ref
  ) => {
    const appConfig = useConfig();
    const isUruguayEnabled = appConfig.isUruguayEnabled ?? false;
    const isClientCategoryTypeEnabled = appConfig.isClientCategoryTypeEnabled ?? false;
    const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
    const isApplicationEffectiveDateEnabled =
      appConfig.isApplicationEffectiveDateEnabled ?? false;
    const isApplicationCreditTypeEnabled =
      appConfig.isApplicationCreditTypeEnabled ?? false;
    const isOriginationDataModalEnabled = appConfig.isOriginationDataModalEnabled ?? false;

    const { clientTheme } = useClientTheme();
    const sectionVariant = clientTheme ? 'primary' : 'success';

    const getActivityDescription = (activityCode: string) => {
      const activity = economicActivities.find(a => String(a.code) === String(activityCode));
      return activity ? activity.label : activityCode || '—';
    };

    const getIvaStatusDescription = (ivaCode: string) => {
      const ivaStatus = ivaStatuses.find(status => String(status.code) === String(ivaCode));
      return ivaStatus ? ivaStatus.label : ivaCode || '—';
    };

    const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
      if (!referrerName) return '—';
      if (typeof referrerName === 'string') {
        const found = referrerNames.find(rn => rn.code === referrerName);
        return found ? found.name : referrerName || '—';
      }
      return referrerName.name || '—';
    };

    const managementLabelPdf = getManagementDisplayLabel(clientData, managements);
    const bd = clientData?.basic_data;

    const formatCurrencyValue = (value: number | undefined): string => {
      if (value === undefined || value === null) return '—';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: localCurrencyCode === CURRENCY_CODES.uyu ? 'UYU' : 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const partnersColumns = [
      { name: 'Nombre', selector: (row: any) => row.first_name || '—', grow: 1 },
      {
        name: isUruguayEnabled ? 'Cédula' : 'Documento',
        selector: (row: any) => row.ci || '—',
        grow: 0.5,
      },
      { name: 'Cargo', selector: (row: any) => row.position || '—', grow: 0.4 },
      {
        name: 'Participación',
        selector: (row: any) => {
          if (isUruguayEnabled) {
            return row.ownership_interest != null && row.ownership_interest !== ''
              ? `${String(row.ownership_interest).replace(/%$/, '')}%`
              : '—';
          }
          return row.ownership_interest || '—';
        },
        grow: 0.5,
      },
      ...(!isUruguayEnabled
        ? [
            { name: 'Bienes', selector: (row: any) => row.assets || '—', grow: 0.8 },
            {
              name: 'Valor',
              selector: (row: any) =>
                row.value ? formatCurrencyValue(row.value) : '—',
              grow: 0.7,
            },
          ]
        : []),
    ];

    const partnersData = clientData?.corporate_structure || [];

    const getProductLineLabel = (productLineCode: string): string => {
      if (!productLineCode) return '—';
      return (
        resolveSelectableProductLineLabel(productLines, productLineCode) ??
        UNAVAILABLE_PRODUCT_LINE_LABEL
      );
    };

    const productsColumns = [
      {
        name: 'Producto',
        selector: (row: applicationData) => getProductLineLabel(row.product_line_code),
        grow: 1,
      },
      {
        name: 'Moneda',
        selector: (row: applicationData) => row.amount?.currency_code || '—',
        grow: 0.5,
      },
      {
        name: 'Monto',
        selector: (row: applicationData) => `$ ${formatCurrency(row.amount?.value ?? 0, true)}`,
        grow: 0.8,
        right: true,
      },
    ];

    const productsData = clientData?.products || [];
    const totals = productsData.reduce(
      (acc, product: applicationData) => {
        const value = Number(product.amount?.value || 0);
        const currencyCode = (product.amount?.currency_code || '').toUpperCase();
        if (
          currencyCode === localCurrencyCode.toUpperCase() ||
          (localCurrencyCode === CURRENCY_CODES.ars && currencyCode === 'ARS') ||
          (localCurrencyCode === CURRENCY_CODES.uyu && currencyCode === 'UYU')
        ) {
          acc.totalLocal += value;
        } else if (currencyCode === CURRENCY_CODES.usd || currencyCode === 'USD') {
          acc.totalUsd += value;
        }
        return acc;
      },
      { totalLocal: 0, totalUsd: 0 }
    );

    const groupedSgrDistributions = sgrDistributions.reduce((acc, row) => {
      const key = row.sgrCode;
      if (!acc[key]) {
        acc[key] = { name: row.sgrName, code: row.sgrCode, rows: [] };
      }
      acc[key].rows.push(row);
      return acc;
    }, {} as Record<string, { name: string; code: string; rows: SgrDistributionResponseItem[] }>);

    const sgrGroups = Object.values(groupedSgrDistributions);

    const sgrGrandTotals = resolveSgrGrandDisplayTotals(
      sgrDistributions,
      localCurrencyCode
    );

    const sgrProductsColumns = [
      {
        name: 'Producto',
        selector: (row: SgrDistributionResponseItem) => row.productLineName || '—',
        grow: 1,
      },
      {
        name: 'Moneda',
        selector: (row: SgrDistributionResponseItem) => row.currencyCode || '—',
        grow: 0.5,
      },
      {
        name: 'Monto',
        selector: (row: SgrDistributionResponseItem) =>
          `$ ${formatCurrency(String(row.amount ?? 0), true)}`,
        grow: 0.8,
        right: true,
      },
    ];

    const getSgrGroupTotals = (rows: SgrDistributionResponseItem[]) => {
      const { ars, usd } = resolveSgrGroupDisplayTotal(rows, localCurrencyCode);
      return { totalArs: ars, totalUsd: usd };
    };

    const renderSectionHeader = (title: string) => (
      <Card variant={sectionVariant} modifier='weak' spacing={'2'} display='flex'>
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          {title}
        </H>
      </Card>
    );

    const renderUySection = (
      title: string,
      content: ReactNode,
      options: { isFirst?: boolean } = {},
    ) => (
      <Card margin={{ top: options.isFirst ? '0' : '4' }} width='100%'>
        {renderSectionHeader(title)}
        <Card margin={{ top: '4' }} width='100%'>
          {content}
        </Card>
      </Card>
    );

    const dgiStatusDisplay =
      normalizeDgiStatus(bd?.dgi_status) || bd?.dgi_status || '—';

    const renderUyResumenBlocks = () => (
      <>
        {renderUySection(
          'Datos Generales',
          <>
            <P type='1' weight='bold' variant='neutral' withMargin={false}>
              {bd?.company_name || '—'}
            </P>
            <Card
              display='flex'
              flexDirection='column'
              gap={UY_PDF_ROW_GAP}
              margin={{ top: '4' }}
              width='100%'
            >
              <PdfFieldRow variant='datosGenerales'>
                <PdfField label='Personería:' value={bd?.legal_entity} />
                <PdfField
                  label='Fecha de constitución:'
                  value={formatSummaryDate(bd?.date_incorporation, '/')}
                />
              </PdfFieldRow>

              <PdfFieldRow variant='datosGenerales'>
                {isClientCategoryTypeEnabled && (
                  <PdfField
                    label='Rubro:'
                    value={clientData.category_type?.name}
                  />
                )}
                <PdfField
                  label='Referido / Vendedor:'
                  value={getReferrerNameLabel(clientData?.referrer_name)}
                />
                {isManagementSelectionEnabled && (
                  <PdfField label='Gerencia:' value={managementLabelPdf} />
                )}
              </PdfFieldRow>

              <PdfFieldRow variant='datosGenerales'>
                <PdfField label='Domicilio fiscal:' value={bd?.company_address} />
                <PdfField label='Departamento:' value={bd?.state} />
                <PdfField label='Localidad:' value={bd?.city} />
                <PdfField label='Código Postal:' value={bd?.postal_code} />
              </PdfFieldRow>

              <PdfFieldRow variant='datosGenerales'>
                <PdfField label='Domicilio legal:' value={bd?.legal_company_address} />
                <PdfField label='Departamento:' value={bd?.legal_state} />
                <PdfField label='Localidad legal:' value={bd?.legal_city} />
                <PdfField label='Código Postal:' value={bd?.legal_postal_code} />
              </PdfFieldRow>

              <Card display='flex' gap='4px' margin={{ top: '2' }}>
                <P type='2' variant='neutral' weight='bold'>
                  Comentarios:
                </P>
                <P type='2' variant='neutral'>
                  {clientData?.extra_comments || '—'}
                </P>
              </Card>

              <PdfFieldRow variant='datosGenerales'>
                <PdfField label='Motivo:' value={clientData?.reason} />
                {isApplicationEffectiveDateEnabled && (
                  <PdfField
                    label='Fecha de vigencia:'
                    value={formatSummaryDate(clientData?.effective_date, '/')}
                  />
                )}
                {isApplicationCreditTypeEnabled && (
                  <PdfField
                    label='Clase de crédito:'
                    value={clientData?.credit_type_name}
                  />
                )}
              </PdfFieldRow>

              <Card display='flex' gap='4px' margin={{ top: '2' }}>
                <P type='2' variant='neutral' weight='bold'>
                  Reseñas:
                </P>
                <P type='2' variant='neutral'>
                  {clientData?.review || '—'}
                </P>
              </Card>
            </Card>
          </>,
          { isFirst: true },
        )}

        {renderUySection(
          'Proveedor del Estado',
          <PdfFieldRow>
            <PdfField
              label='Vínculo contractual:'
              value={formatBooleanAsYesNo(bd?.contractual_link)}
            />
            <PdfField
              label='Persona políticamente expuesta:'
              value={formatBooleanAsYesNo(bd?.politically_exposed)}
            />
            <PdfField
              label='Registro Único de Proveedores del Estado (RUPE):'
              value={formatBooleanAsYesNo(bd?.state_supplier_registry)}
            />
          </PdfFieldRow>,
        )}

        {renderUySection(
          'Inscripción DGI',
          <PdfFieldRow>
            <PdfField
              label='Fecha de emisión:'
              value={formatSummaryDate(bd?.dgi_issue_date)}
            />
            <PdfField label='Estado DGI:' value={dgiStatusDisplay} />
            <PdfField
              label='Fecha de vencimiento:'
              value={formatSummaryDate(bd?.dgi_expiration_date)}
            />
          </PdfFieldRow>,
        )}

        {renderUySection(
          'Certificado común BPS',
          <PdfFieldRow>
            <PdfField
              label='Fecha de vencimiento:'
              value={formatSummaryDate(bd?.bps_expiration_date)}
            />
            <PdfField
              label='Fecha de emisión:'
              value={formatSummaryDate(bd?.bps_issue_date)}
            />
            <PdfField
              label='Número certificado BPS:'
              value={bd?.bps_certificate_number}
            />
          </PdfFieldRow>,
        )}
      </>
    );

    const renderArGeneralData = () => (
      <>
        <Card variant='neutral' modifier='inverted' spacing={'2'}>
          <H type='2' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
            Datos Generales
          </H>
        </Card>

        <Card margin={{ top: '3' }}>
          <P type='1' weight='bold' variant='neutral'>
            {clientData?.basic_data?.company_name || '—'}
          </P>
        </Card>

        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Personería:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.legal_entity || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Fecha de constitución:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.date_incorporation || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Condición frente al IVA:</P>
            <P type='2' variant='neutral'>{getIvaStatusDescription(clientData?.basic_data?.iva_status)}</P>
          </Card>
        </Card>

        <Card display='flex' gap='4px' width='100%' margin={{ top: '2' }}>
          <P type='2' variant='neutral' weight='bold'>Actividad económica:</P>
          <P type='2' variant='neutral'>{getActivityDescription(clientData?.basic_data?.activity)}</P>
        </Card>

        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Domicilio fiscal:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.company_address || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Localidad:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.city || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Provincia:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.state || '—'}</P>
          </Card>
        </Card>

        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Teléfono:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.phone_number || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Correo electrónico:</P>
            <P type='2' variant='neutral'>{clientData?.basic_data?.email || '—'}</P>
          </Card>
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>Referido/Vendedor:</P>
            <P type='2' variant='neutral'>{getReferrerNameLabel(clientData?.referrer_name)}</P>
          </Card>
        </Card>

        <Card display='flex' gap='4px' margin={{ top: '2' }}>
          <P type='2' variant='neutral' weight='bold'>Motivo:</P>
          <P type='2' variant='neutral'>{clientData?.reason || '—'}</P>
        </Card>

        {(clientData?.operation_type_label ||
          clientData?.operation_type_id ||
          isOriginationDataModalEnabled) && (
          <Card display='flex' gap='4px' margin={{ top: '2' }}>
            <P type='2' variant='neutral' weight='bold'>Tipo de operación:</P>
            <P type='2' variant='neutral'>{clientData?.operation_type_label || '—'}</P>
          </Card>
        )}

        {isOriginationDataModalEnabled && (
          <Card display='flex' gap='4px' margin={{ top: '2' }}>
            <P type='2' variant='neutral' weight='bold'>Saldo vigente con la SGR:</P>
            <P type='2' variant='neutral'>
              {clientData?.current_sgr_balance != null
                ? `$ ${formatCurrency(clientData.current_sgr_balance, true)}`
                : '—'}
            </P>
          </Card>
        )}

        <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
          {isManagementSelectionEnabled ? (
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Gerencia:</P>
              <P type='2' variant='neutral'>{managementLabelPdf}</P>
            </Card>
          ) : (
            <Card display='flex' gap='4px' width='30%'>
              <P type='2' variant='neutral' weight='bold'>Oficial:</P>
              <P type='2' variant='neutral'>
                {getOfficerDisplayLabel(clientData?.officer, officers)}
              </P>
            </Card>
          )}
          <Card display='flex' gap='4px' width='60%'>
            <P type='2' variant='neutral' weight='bold'>Comentarios:</P>
            <P type='2' variant='neutral'>{clientData?.extra_comments || '—'}</P>
          </Card>
        </Card>

        <Card display='flex' gap='4px' margin={{ top: '2' }}>
          <P type='2' variant='neutral' weight='bold'>Reseñas:</P>
          <P type='2' variant='neutral'>{clientData?.review || '—'}</P>
        </Card>
      </>
    );

    return (
      <div
        ref={ref}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          pointerEvents: 'none',
          width: '1200px',
          background: '#fff',
          padding: '24px',
        }}
      >
        <Card spacing={'3'}>
          {isUruguayEnabled ? renderUyResumenBlocks() : renderArGeneralData()}

          {productsData.length > 0 && (
            <Card margin={{ top: '4' }}>
              <Card variant={sectionVariant} modifier='weak' spacing={'2'}>
                <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                  Solicitud
                </H>
              </Card>

              <Card display='flex' gap='4px' margin={{ top: '2' }}>
                <P type='2' variant='neutral' weight='bold'>Motivo:</P>
                <P type='2' variant='neutral'>{clientData?.reason || '—'}</P>
              </Card>

              {(clientData?.operation_type_label || clientData?.operation_type_id) && (
                <Card display='flex' gap='4px' margin={{ top: '2' }}>
                  <P type='2' variant='neutral' weight='bold'>Tipo de operación:</P>
                  <P type='2' variant='neutral'>{clientData?.operation_type_label || '—'}</P>
                </Card>
              )}

              <Card display='flex' justifyContent='space-between' alignItems='flex-start' margin={{ top: '2' }} gap='20px'>
                <Card width='60%'>
                  <Table
                    columns={productsColumns}
                    data={productsData}
                    tableData={{ size: productsData.length, totalElements: productsData.length }}
                    variant='weak'
                  />
                </Card>

                <Card
                  width='35%'
                  border={true}
                  variant={sectionVariant}
                  modifier='weak'
                  spacing={'2'}
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                >
                  <H type='2' withMargin={false} weight='bold' variant={sectionVariant} align='center'>
                    {localCurrencyCode} {formatCurrency(totals.totalLocal)}
                  </H>
                  <H type='2' withMargin={false} weight='bold' variant={sectionVariant} align='center'>
                    {CURRENCY_CODES.usd} {formatCurrency(totals.totalUsd)}
                  </H>
                </Card>
              </Card>
            </Card>
          )}

          {creditoMaximoState && (
            <Card margin={{ top: '4' }}>
              <RiskCreditoMaximoReadOnly
                mode='risk-analysis'
                creditoState={creditoMaximoState}
                productLines={creditoProductLines}
                superaMaximo={creditoSuperaMaximo}
                localCurrencyCode={localCurrencyCode}
                isUruguayEnabled={isUruguayEnabled}
                isQuotationsEnabled={isQuotationsEnabled}
                availableAssets={creditoAvailableAssets}
                includeSolicitud={false}
              />
            </Card>
          )}

          {sgrDistributions.length > 0 && (
            <Card margin={{ top: '4' }}>
              <Card variant={sectionVariant} modifier='weak' spacing={'2'}>
                <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                  Monto Aprobado
                </H>
              </Card>

              <Card display='flex' gap='4px' margin={{ top: '2' }}>
                <P type='2' variant='neutral' weight='bold'>Motivo:</P>
                <P type='2' variant='neutral'>{clientData?.reason || '—'}</P>
              </Card>

              {sgrGroups.map((group) => {
                const groupTotals = getSgrGroupTotals(group.rows);
                return (
                  <Card key={group.code} margin={{ top: '3' }}>
                    <P type='2' variant='neutral' weight='bold' withMargin={false}>
                      {group.name}
                    </P>
                    <Card display='flex' justifyContent='space-between' alignItems='flex-start' margin={{ top: '2' }} gap='20px'>
                      <Card width='60%'>
                        <Table
                          columns={sgrProductsColumns}
                          data={group.rows}
                          tableData={{ size: group.rows.length, totalElements: group.rows.length }}
                          variant='weak'
                        />
                      </Card>
                      <Card
                        width='35%'
                        border={true}
                        variant={sectionVariant}
                        modifier='weak'
                        spacing={'2'}
                        display='flex'
                        flexDirection='column'
                        justifyContent='center'
                        alignItems='center'
                        gap='8px'
                      >
                        <P type='2' variant='neutral' withMargin={false}>
                          Total aprobado en ({group.name}):
                        </P>
                        <H type='2' withMargin={false} weight='bold' variant={sectionVariant} align='center'>
                          {localCurrencyCode} {formatCurrency(String(groupTotals.totalArs))}
                        </H>
                        <H type='2' withMargin={false} weight='bold' variant={sectionVariant} align='center'>
                          {CURRENCY_CODES.usd} {formatCurrency(String(groupTotals.totalUsd))}
                        </H>
                      </Card>
                    </Card>
                  </Card>
                );
              })}

              <Card margin={{ top: '3' }}>
                <Card
                  variant={sectionVariant}
                  modifier='weak'
                  spacing={'2'}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  gap='32px'
                >
                  <Card display='flex' flexDirection='column' alignItems='flex-end'>
                    <H type='3' variant='neutral-1' weight='bold' withMargin={false}>
                      Total aprobado:
                    </H>
                    <P type='2' variant='neutral' modifier='weak' withMargin={false}>
                      Suma de todas las SGR
                    </P>
                  </Card>
                  <Card display='flex' flexDirection='column' alignItems='flex-start' gap='4px'>
                    <H type='2' withMargin={false} weight='bold' variant={sectionVariant}>
                      {localCurrencyCode} {formatCurrency(String(sgrGrandTotals.ars))}
                    </H>
                    <H type='2' withMargin={false} weight='bold' variant={sectionVariant}>
                      {CURRENCY_CODES.usd} {formatCurrency(String(sgrGrandTotals.usd))}
                    </H>
                  </Card>
                </Card>
              </Card>
            </Card>
          )}

          {partnersData.length > 0 && (
            <Card margin={{ top: '4' }}>
              <Card variant={sectionVariant} modifier='weak' spacing={'2'}>
                <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                  Socios
                </H>
              </Card>
              <Card margin={{ top: '2' }}>
                <Table
                  columns={partnersColumns}
                  data={partnersData}
                  tableData={{ size: partnersData.length, totalElements: partnersData.length }}
                  variant='weak'
                />
              </Card>
            </Card>
          )}

          <Card margin={{ top: '4' }}>
            <Card variant={sectionVariant} modifier='weak' spacing={'2'}>
              <H type='3' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
                Documentación
              </H>
            </Card>
            <Card margin={{ top: '3' }}>
              <P type='1' weight='bold' variant='neutral'>
                Documentación
              </P>
              <Card
                display='flex'
                flexWrap='wrap'
                gap='20px'
                justifyContent='space-between'
                margin={{ top: '3' }}
              >
                {legalEntities
                  .find((item) => item.label === clientData?.basic_data?.legal_entity)
                  ?.requiredDocuments.map((document, index) => {
                    const isDocumentUploaded = clientData?.files?.find(
                      (item) => item?.type === document.id
                    )?.is_basic_data;

                    return (
                      <Card
                        key={index}
                        width='48%'
                        display='flex'
                        height='auto'
                        alignItems='center'
                        gap='16px'
                      >
                        {isDocumentUploaded ? (
                          <Icon name='done_outline' variant='success' />
                        ) : (
                          <Icon name='cancel_outline' variant='danger' />
                        )}
                        <Card width='-webkit-fill-available'>
                          <P
                            withMargin={false}
                            type='2'
                            variant='neutral'
                            align='left'
                            modifier={isDocumentUploaded ? undefined : 'weak'}
                          >
                            {document.value}
                          </P>
                        </Card>
                        {isDocumentUploaded && (
                          <Card
                            display='flex'
                            justifyContent='center'
                            width='60px'
                            border={true}
                            spacing={'1'}
                          >
                            <Icon
                              name='view_outline'
                              height={32}
                              variant='neutral'
                            />
                          </Card>
                        )}
                      </Card>
                    );
                  })}
              </Card>

              {clientData?.files?.some((doc) => !doc.is_basic_data) && (
                <Card margin={{ top: '4' }}>
                  <P type='1' weight='bold' variant='neutral'>
                    Documentación extra
                  </P>
                  <Card
                    display='flex'
                    flexWrap='wrap'
                    gap='20px'
                    justifyContent='space-between'
                    margin={{ top: '2' }}
                  >
                    {clientData?.files?.map(
                      (document, index) =>
                        !document.is_basic_data && (
                          <Card
                            key={index}
                            width='48%'
                            display='flex'
                            alignItems='center'
                            gap='16px'
                          >
                            <Icon name='done_outline' variant='success' />
                            <Card width='-webkit-fill-available'>
                              <P
                                withMargin={false}
                                type='2'
                                variant='neutral'
                                align='left'
                              >
                                {otherLegalEntities.find(
                                  (item) => item.id === document.type
                                )?.label || document.type}
                              </P>
                            </Card>
                            <Card
                              display='flex'
                              justifyContent='center'
                              width='60px'
                              border={true}
                              spacing={'1'}
                            >
                              <Icon
                                name='view_outline'
                                height={32}
                                variant='neutral'
                              />
                            </Card>
                          </Card>
                        )
                    )}
                  </Card>
                </Card>
              )}
            </Card>
          </Card>
        </Card>

        <Card margin={{ top: '4' }}>
          {isUruguayEnabled && (
            <LidecoSemaforo
              riskData={Array.isArray(riskData) ? riskData : undefined}
              isLoading={false}
              onRefresh={() => { }}
              hideRefresh
            />
          )}
          <CheckLightResume
            data={riskData}
            showBalanceInfo={true}
            isLoading={false}
            handleRiskData={() => { }}
            handleRiskDataAndDebts={() => { }}
            riskCodes={[]}
            debts={debts}
            documents={documents}
            cuit={cuit}
            hideInfo={false}
            hideArBureauSections={isUruguayEnabled}
            balanceData={balanceData}
            postBalanceData={postBalanceData}
            isPdfMode
            showDualBalance={showDualBalance}
            previousBalanceData={previousBalanceData}
            previousBpbIndicators={previousBpbIndicators}
          />
        </Card>
      </div>
    );
  }
);

export default RiskAnalysisPdf;
